<template>
  <div class="bg-gray-200 -safe-margin-bottom safe-padding-bottom">
    <div
      class="max-w-7xl mx-auto p-2 sm:p-4 sm:px-6 lg:px-8 min-h-dvh-fallback"
    >
      <div
        v-if="eventsStore.event"
        class="max-w-6xl mx-auto p-4 sm:p-6 bg-white bg-opacity-80 rounded-xl px-3 sm:px-10 py-6"
      >
        <div v-if="!successInvoiceOrder">
          <div class="flex flex-wrap">
            <div class="w-full md:w-1/2 lg:w-3/5">
              <div class="flex justify-between items-center gap-4">
                <h1 class="font-bold text-xl">
                  {{ eventsStore.event?.name }}
                </h1>
                <BaseLocaleLink
                  look="link"
                  theme="light"
                  class="font-normal hidden sm:inline-flex"
                  size="xs"
                  :to="eventRoute"
                >
                  <BaseIcon
                    name="outline_arrow_turn_left"
                    size="xs"
                    class="mr-2"
                  />
                  <span class="text-sm">
                    {{
                      $t(
                        `events.back_to_${
                          EventTypeNamesMap[eventsStore.event.type]
                        }`
                      )
                    }}
                  </span>
                </BaseLocaleLink>
              </div>
              <template v-for="ticket in ticketsDetails" :key="ticket.id">
                <div
                  v-if="order[ticket.id]"
                  class="p-3 pr-1 sm:p-4 sm:pr-3 bg-white rounded-xl mt-3 flex justify-between items-center"
                >
                  <div class="flex flex-col items-start mr-3">
                    <div class="font-bold">
                      {{ ticket.name }}
                    </div>
                    <div
                      v-if="ticket.subtitle"
                      class="text-xs text-gray-500 sm:rounded-xl sm:border sm:border-gray-300 sm:py-1 sm:px-2.5"
                    >
                      {{ ticket.subtitle }}
                    </div>
                  </div>
                  <div class="flex items-center py-3">
                    <div class="mr-3">
                      {{ getTicketPriceFormatted(ticket) }}
                    </div>
                    <BaseButton
                      rounded="full"
                      look="none"
                      size="xs"
                      class="text-gray-500 hover:opacity-80"
                      :class="[
                        !order[ticket.id] && 'pointer-events-none opacity-20',
                      ]"
                      @click="removeTicket(ticket.id)"
                    >
                      <BaseIcon name="outline_minus_circle" size="md" />
                    </BaseButton>
                    <div
                      class="sm:mx-2 text-center font-bold"
                      :class="[order[ticket.id] && 'text-primary-500']"
                      style="min-width: 2ch"
                    >
                      {{ order[ticket.id] || 0 }}
                    </div>
                    <BaseButton
                      rounded="full"
                      look="none"
                      size="xs"
                      class="text-gray-500 hover:opacity-50"
                      @click="addTicket(ticket.id)"
                    >
                      <BaseIcon name="outline_plus_circle" size="md" />
                    </BaseButton>
                  </div>
                </div>
              </template>
              <div
                v-if="!ticketsInOrder"
                class="flex items-center justify-center h-full"
              >
                <div
                  class="flex justify-center flex-col items-center space-y-2 py-16"
                >
                  <div class="text-sm text-gray-400 text-center">
                    {{
                      $t(
                        `tickets.no_tickets_selected_${
                          EventTypeNamesMap[eventsStore.event.type]
                        }`
                      )
                    }}
                  </div>
                  <BaseLocaleLink :to="eventRoute">
                    <BaseButton size="sm">
                      {{
                        $t(
                          `tickets.go_to_${
                            EventTypeNamesMap[eventsStore.event.type]
                          }`
                        )
                      }}
                    </BaseButton>
                  </BaseLocaleLink>
                </div>
              </div>
            </div>
            <div class="w-full md:w-1/2 lg:w-2/5">
              <div
                class="bg-white bg-opacity-90 rounded-xl mt-3 md:mt-0 md:ml-3 px-3 md:px-6 py-6"
              >
                <div class="flex justify-between items-center">
                  <h2 class="font-bold text-xl">
                    {{ $t('tickets.checkout') }}
                  </h2>
                  <!-- <BaseSelect
                    v-model="currency"
                    :options="currencyOptions"
                    name="currency"
                  /> -->
                </div>
                <hr class="pb-3 mt-3" />
                <template v-if="ticketsInOrder">
                  <template v-for="ticket in ticketsDetails" :key="ticket.id">
                    <div
                      v-if="order[ticket.id]"
                      class="flex items-center justify-between py-3"
                    >
                      <div class="w-1/2">
                        {{ ticket.name }}
                        <div
                          v-if="ticket.subtitle"
                          class="text-xs text-gray-500"
                        >
                          {{ ticket.subtitle }}
                        </div>
                      </div>
                      <div class="w-1/4 text-center">
                        {{ order[ticket.id] }}
                      </div>
                      <div class="w-1/4 text-right">
                        {{ getTicketPriceFormatted(ticket, order[ticket.id]) }}
                      </div>
                    </div>
                  </template>
                </template>
                <div v-else class="text-sm text-gray-400">
                  {{ $t('tickets.select_ticket') }}
                </div>
                <hr class="pb-3 mt-3" />
                <div class="pl-4 mb-2 text-md">
                  {{ $t('tickets.promo_code') }}
                </div>
                <div class="flex items-start">
                  <BaseInput
                    v-model="promoCode"
                    class="w-full mr-3 min-w-0 overflow-hidden"
                    name="promo"
                    :disabled="isPromoCodeApplied || isPromoCodeProcessing"
                    :placeholder="$t('tickets.promo_placeholder')"
                    @keydown.enter="applyPromoCode"
                  />
                  <BaseButton
                    v-if="isPromoCodeApplied"
                    look="border"
                    theme="white"
                    size="md"
                    :loading="isPromoCodeProcessing"
                    @click="removePromoCode"
                  >
                    {{ $t('tickets.promo_remove') }}
                  </BaseButton>
                  <BaseButton
                    v-else
                    look="border"
                    theme="white"
                    size="md"
                    :loading="isPromoCodeProcessing"
                    :class="[!promoCode && 'opacity-30 pointer-events-none']"
                    @click="applyPromoCode"
                  >
                    {{ $t('tickets.promo_apply') }}
                  </BaseButton>
                </div>
                <div v-if="isPromoCodeApplied" class="text-sm pl-4 mt-1">
                  <span v-if="discountValue !== 0" class="text-green-500">
                    {{ $t('tickets.promo_applied') }}
                  </span>
                  <span v-else class="text-amber-500">
                    {{ $t('tickets.promo_not_applicable') }}
                  </span>
                </div>
                <div class="flex justify-end flex-wrap">
                  <div class="w-full text-right mt-10">
                    <div
                      v-if="discountValue !== 0"
                      class="flex justify-between mb-3"
                    >
                      <div>
                        {{ $t('tickets.discount') }}
                      </div>
                      <div>
                        {{ discountValueFormatted }}
                      </div>
                    </div>
                    <div class="flex justify-between mb-3">
                      <div class="font-bold">
                        {{ $t('tickets.price') }}
                      </div>
                      <div class="font-bold">
                        {{ totalValueFormatted }}
                      </div>
                    </div>
                    <div
                      v-if="totalValue"
                      class="flex justify-between mb-3 text-xs text-left text-gray-600"
                    >
                      <!-- <div>
                          {{ $t('tickets.vat') }}
                        </div>
                        <div>
                          {{ isOnlyOfflineTicketsInCart ? '+5%' : '+20%' }}
                        </div> -->
                      {{ $t('tickets.vat_disclaimer') }}
                    </div>
                  </div>
                  <div class="w-full flex flex-wrap justify-end space-x-2 mt-6">
                    <div
                      v-if="authStore.auth"
                      class="flex gap-2 flex-wrap justify-end"
                    >
                      <BaseButton
                        :loading="isFormProcessing"
                        :disabled="!ticketsInOrder"
                        :class="[isFormProcessing && 'pointer-events-none']"
                        class="!h-auto leading-5 py-2.5"
                        @click="() => onNextClick(false)"
                      >
                        <div class="whitespace-pre-wrap">
                          {{ $t('tickets.pay_by_individual') }}
                        </div>
                      </BaseButton>
                      <BaseButton
                        look="border"
                        :loading="isFormProcessing"
                        :disabled="!ticketsInOrder"
                        :class="[isFormProcessing && 'pointer-events-none']"
                        class="!h-auto leading-5 py-2.5"
                        @click="() => onNextClick(true)"
                      >
                        <div class="whitespace-pre-wrap">
                          {{ $t('tickets.pay_by_company') }}
                        </div>
                      </BaseButton>
                    </div>
                    <template v-else>
                      <BaseButton
                        :disabled="!ticketsInOrder"
                        @click="authStore.openLoginModal"
                      >
                        {{ $t('common.auth_to_continue') }}
                      </BaseButton>
                    </template>
                  </div>

                  <i18n-t
                    keypath="tickets.disclaimer"
                    tag="div"
                    class="text-gray-500 text-xs text-right mt-3"
                  >
                    <template #terms>
                      <BaseLink
                        href="https://wnhub.io/terms-conditions"
                        target="_blank"
                      >
                        {{ $t('tickets.disclaimer_terms') }}
                      </BaseLink>
                    </template>
                    <template #br><br /></template>
                    <template #privacy>
                      <BaseLink
                        href="https://wnhub.io/privacy-policy"
                        target="_blank"
                      >
                        {{ $t('tickets.disclaimer_privacy') }}
                      </BaseLink>
                    </template>
                  </i18n-t>
                </div>
              </div>
            </div>
          </div>
        </div>
        <OrdersPaymentInvoiceBlock v-else :order-route="orderRoute" />
      </div>
    </div>
    <TicketsPaymentMethodModal
      :is-open="isPaymentModalOpen"
      :country="authStore.profile?.country"
      show-offer
      :event-slug="eventsStore.event?.slug"
      :event-type="eventsStore.event?.type"
      :is-company="isCompany"
      @close="isPaymentModalOpen = false"
      @pay="submit"
    />
  </div>
  <PaymentXSolla
    ref="XSollaComponent"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
  <PaymentStripe
    ref="StripeComponent"
    :order-id="orderResponse?.id"
    :total="stripeValue"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
  <PaymentCloudPayments
    ref="CloudPaymentsComponent"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
</template>

<script setup lang="ts">
/*
We may be want to consider to refactor this component
TODO:
- Move order and order actions to the state
- Split to the components: this one as wrapper, right panel, ticket item, success page
*/
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { languageMap } from '~/models/locale'
import { EventTypeNamesMap } from '~/models/events'
import { useOrders } from '~/stores/orders'
import {
  Currency,
  OrderPaymentType,
  type OrderResponse,
  type CompanyRequisites,
} from '~/models/orders'
import { useNotifications } from '~/stores/notifications'
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import { PaymentSystem } from '~/models/payment'
import PaymentXSolla from '~/components/payment/PaymentXSolla.vue'
import PaymentStripe from '~/components/payment/PaymentStripe.vue'
import PaymentCloudPayments from '~/components/payment/PaymentCloudPayments.vue'
import { YandexECommerceEventType } from '~/models/ecommerce-yandex'
import { YandexMetrikaGoals } from '~/plugins/yandex-metrika/index.client'
import type { GoogleECommerceItem } from '~/models/ecommerce-google'

const CloudPaymentsComponent = ref<null | InstanceType<
  typeof PaymentCloudPayments
>>(null)
const StripeComponent = ref<null | InstanceType<typeof PaymentStripe>>(null)

const authStore = useAuth()
const { pushNotification } = useNotifications()
const { t } = useI18n()
const route = useRoute()
const i18n = useI18n()
const { removeSessionStorageValue } = useSessionStorage()
const {
  $yandexMetrika: {
    reachGoal,
    sendYandexECommerceEvent,
    getYandexECommerceProductFromTicket,
  },
  $linkedin: { lintrk },
  $facebook: { fbq },
  $gtm: {
    gTagPush,
    sendAddToCartGoogleECommerceEvent,
    sendRemoveFromCartGoogleECommerceEvent,
    sendViewCartGoogleECommerceEvent,
    getGoogleECommerceItemFromTicket,
    sendBeginCheckoutGoogleECommerceEvent,
    sendAddPaymentInfoGoogleECommerceEvent,
  },
  $cityads: { trackClickIdOrder, trackOrder, trackProducts },
} = useNuxtApp()
const eventSlug = Array.isArray(route.params.event)
  ? String(route.params.event[0])
  : String(route.params.event)

const lang = i18n.locale.value
const { ticketsDetails, ticketsDetailsMap } = storeToRefs(useOrders())
const { fetchTicketListDetails, createOrder, checkPromocode, formatPrice } =
  useOrders()
const eventsStore = useEvents()
const XSollaComponent = ref<null | InstanceType<typeof PaymentXSolla>>(null)

// useState to avoid hydration missmatch as we don't store orders in the pinia store
const order = useState<{ [key: number]: number }>(() => ({}))

const promoCode = ref('')
const isPromoCodeProcessing = useState<boolean>(() => false)
const orderResponse = ref<OrderResponse | null>(null)
const promoCodeDiscount = reactive<{ [key: string]: number }>({})
const isPromoCodeApplied = ref(false)
const currency = useState<Currency>(() => Currency.USD)
const isFormProcessing = ref(false)
const isRequestInvoice = ref(false)
const isPaymentModalOpen = ref(false)
const isNavigated = ref(false)
const isCompany = ref(false)
const successInvoiceQuery = 'success-invoice'
// const currencyOptions = computed(() => {
//   return [
//     { label: 'EUR', value: 'EUR' },
//     { label: 'USD', value: 'USD' },
//     { label: 'RUB', value: 'RUB' },
//   ]
// })

const successInvoiceOrder = computed(() => {
  return useRouter().currentRoute.value.query[successInvoiceQuery]
})

const ticketsInOrder = computed(() => {
  return Object.keys(order.value).reduce(
    (total, id) => total + order.value[Number(id)] || 0,
    0
  )
})

const totalValue = computed(() => {
  return Object.keys(order.value).reduce(
    (total, id) =>
      total +
      (order.value[Number(id)] || 0) *
        ticketsDetailsMap.value[Number(id)][
          currency.value.toLowerCase() as Lowercase<Currency>
        ],
    0
  )
})

const stripeValue = computed(() => {
  return orderResponse?.value?.amount
    ? formatPrice(
        (orderResponse.value.amount + (orderResponse.value?.vat || 0)) / 100,
        currency.value,
        languageMap[lang]
      )
    : ''
})

const totalValueFormatted = computed(() => {
  return formatPrice(
    totalValue.value - (promoCodeDiscount[currency.value] / 100 || 0),
    currency.value,
    languageMap[lang]
  )
})

const discountValue = computed(() => {
  return -(promoCodeDiscount[currency.value] || 0)
})

const discountValueFormatted = computed(() => {
  return formatPrice(
    discountValue.value / 100,
    currency.value,
    languageMap[lang]
  )
})
// TODO: use once back is released
// const isOnlyOfflineTicketsInCart = computed(() => {
//   const hubTickets = Object.keys(order.value).reduce((acc, ticketId) => {
//     // we don't have features in tickets, so we need to find hub ticket in ticketsDetails list
//     const wnconfTicketId = Number(ticketId)
//     const ticket = tickets.value.find((item) => item.id === wnconfTicketId) // finds ticket from conf/billing

//     if (ticket) {
//       const hubTicketId = ticket.hub_ticket_id
//       const hubTicket = ticketsDetails.value.find(
//         (item) => item.id === hubTicketId
//       ) // finds ticket from hub with features

//       if (hubTicket) {
//         acc.push(hubTicket)
//       }
//     }

//     return acc
//   }, [] as TicketDetails[])

//   return hubTickets.every((ticket) =>
//     ticket?.features.some((item) => item.feature === 'offline')
//   )
// })

function addTicket(id: number, sendECommerce = true) {
  if (!order.value[id]) {
    order.value[id] = 0
  }
  order.value[id]++

  if (isPromoCodeApplied.value) {
    applyPromoCode()
  }
  updateQuery()

  if (sendECommerce) {
    sendECommerceQuantityEvent(id, 1)
  }
}

function removeTicket(id: number) {
  if (order.value[id]) {
    order.value[id]--
  }
  if (isPromoCodeApplied.value) {
    applyPromoCode()
  }
  updateQuery()

  sendECommerceQuantityEvent(id, -1)
}

function getTicketPriceFormatted(ticket: any, count = 1) {
  return formatPrice(
    ticket[currency.value.toLowerCase() as Lowercase<Currency>] * count,
    currency.value,
    languageMap[lang]
  )
}

async function applyPromoCode() {
  if (!promoCode.value) {
    return
  }

  const ticketsData = ticketsDetails.value.map((ticket) => ({
    id: ticketsDetailsMap.value[Number(ticket.id)].id,
    count: order.value[Number(ticket.id)] || 0,
  }))

  try {
    isPromoCodeProcessing.value = true
    const discount = await checkPromocode(
      promoCode.value,
      eventsStore.event?.id as number,
      ticketsData
    )
    Object.assign(promoCodeDiscount, discount)
    isPromoCodeApplied.value = true
    updateQuery()
  } catch (error: any) {
    pushNotification({
      title: t('notifications.tickets.promocode_error'),
      theme: 'destructive',
    })
    useLogError(error)
  } finally {
    isPromoCodeProcessing.value = false
  }
}

function removePromoCode() {
  isPromoCodeApplied.value = false
  promoCode.value = ''
  Object.keys(promoCodeDiscount).forEach((key) => delete promoCodeDiscount[key])
  updateQuery()
}

function onPaymentClose() {
  isFormProcessing.value = false
  isNavigated.value = false
}

async function onPaymentDone() {
  await navigateTo(
    useLocaleRoute()(
      `/users/me/orders/${orderResponse.value?.id}?status=success`
    )
  )
}

async function submit(
  isInvoice: boolean,
  isCryptoInvoice: boolean,
  countryId: string,
  isFree = false,
  requisites?: CompanyRequisites
) {
  isRequestInvoice.value = isInvoice

  const currencyCode = countryId === 'RU' ? 'RUB' : currency.value

  let paymentType = OrderPaymentType.Card

  if (isFree) {
    paymentType = OrderPaymentType.Free
  } else if (isInvoice) {
    paymentType = isCryptoInvoice
      ? OrderPaymentType.Crypto
      : OrderPaymentType.Invoice
  }

  const orderRequest = {
    event_id: eventsStore.event?.id as number,
    currency: currencyCode,
    payment_type: paymentType,
    is_request_invoice: isInvoice,
    is_crypto_invoice: isCryptoInvoice,
    ...(requisites && { requisites }),
    ...(promoCode.value &&
      isPromoCodeApplied.value && { promo_code: promoCode.value }),
    items: Object.keys(order.value).map((ticketId) => ({
      entity_type: 7,
      entity_id: ticketsDetailsMap.value[Number(ticketId)].id,
      quantity: order.value[Number(ticketId)],
    })),
  }

  if (paymentType === OrderPaymentType.Card) {
    lintrk('track', { conversion_id: 17206314 })
    fbq('track', 'InitiateCheckout')
    gTagPush({ event: 'InitiateCheckout' })
  }

  let ecommercePaymentType = 'card'

  if (isFree) {
    ecommercePaymentType = 'free'
  } else if (isInvoice) {
    ecommercePaymentType = isCryptoInvoice
      ? 'crypto-invoice'
      : 'request-invoice'
  }

  sendECommerceAddPaymentInfoEvent(ecommercePaymentType)

  try {
    isFormProcessing.value = true

    orderResponse.value = await createOrder(orderRequest)

    const cityIdTotal = orderResponse.value?.amount
      ? (orderResponse.value?.amount / 100).toFixed(2)
      : '0.00'
    const cityIdCurrency = orderResponse.value?.currency || 'USD'
    trackClickIdOrder(
      authStore.profile?.id + '_' + orderResponse.value?.id,
      cityIdTotal,
      cityIdCurrency
    )
    trackOrder(orderResponse.value?.id, cityIdTotal, cityIdCurrency, [
      orderResponse.value?.event_id,
    ])

    if (orderResponse.value?.payment_system_type === PaymentSystem.XSolla) {
      isNavigated.value = true

      await XSollaComponent.value?.processXSollaOrder(
        orderResponse.value.payment_system_data?.token as string
      )
      return
    }

    if (orderResponse.value?.payment_system_type === PaymentSystem.Stripe) {
      isNavigated.value = true

      await StripeComponent.value?.processStripePayment(
        orderResponse.value?.payment_system_data?.token as string
      )
      return
    }

    if (
      orderResponse.value?.payment_system_type === PaymentSystem.CloudPayments
    ) {
      isNavigated.value = true

      await CloudPaymentsComponent.value?.processCloudPayments(
        {
          amount:
            (orderResponse.value?.amount || 0) +
            (orderResponse.value?.vat || 0),
          invoiceId: String(orderResponse.value?.id),
          description: eventsStore.event?.name,
        },
        requisites
      )
      return
    }

    if (orderResponse.value?.payment_type === OrderPaymentType.Free) {
      isNavigated.value = true
      await navigateTo(
        useLocaleRoute()(
          `/users/me/orders/${orderResponse.value?.id}?status=success`
        )
      )
    } else {
      await useRouter().push({
        query: { [successInvoiceQuery]: orderResponse.value?.id },
      })
    }
  } catch (error) {
    pushNotification({
      title: t('notifications.tickets.submit_error'),
      theme: 'destructive',
    })
    useLogError(error)
  } finally {
    // We want to show processing animation until the real browser navigation
    !isNavigated.value && (isFormProcessing.value = false)
  }
}

// function resetForm() {
//   isFormProcessing.value = false
//   isRequestInvoice.value = false
//   paymentLink.value = null
//   order.value = {}
//   removePromoCode()
// }

async function parseQuery() {
  if (route.query.promo) {
    if (process.server) {
      isPromoCodeProcessing.value = true
    } else {
      promoCode.value = String(route.query.promo) as string
      await applyPromoCode()
    }
  }

  if (ticketsInOrder.value) {
    return
  }
  ticketsDetails.value.forEach((ticket) => {
    if (route.query[`tickets[${ticket.id}]`]) {
      let count = Number(route.query[`tickets[${ticket.id}]`])
      while (count) {
        addTicket(ticket.id, false)
        count--
      }
    }
  })
}

function updateQuery() {
  const map = Object.keys(order.value).reduce(
    (map, item) => ({
      ...map,
      [`tickets[${item}]`]: order.value[Number(item)],
    }),
    {}
  )

  useRouter().replace({
    query: {
      ...map,
      ...(isPromoCodeApplied.value &&
        promoCode.value && { promo: promoCode.value as string }),
    },
  })
}

const eventRoute = computed(() =>
  eventsStore.event?.slug
    ? eventsStore.getEventPageRoute(
        eventsStore.event?.slug,
        eventsStore.event?.type
      )
    : ''
)

const orderRoute = computed(
  () =>
    useLocaleRoute()(
      `/users/me/orders/${orderResponse.value?.id || successInvoiceOrder.value}`
    )?.path
)

async function load() {
  try {
    await eventsStore.fetchEvent(eventSlug, false)
    await fetchTicketListDetails(eventsStore.event?.id)
    currency.value = eventsStore.currency
    await parseQuery()
    // we ran console.info with ticketsInOrder.value to avoid strange bug
    // when computed not recalculated based on useState datа without explicit access
    // TODO: create reproduce for nuxt team
    console.info('total tickets loaded:', ticketsInOrder.value)
    trackProducts([eventsStore.event?.id as number], [ticketsInOrder.value])

    sendECommerceViewCartEvent()
  } catch (error: any) {
    throw useCreateScreenError({ message: 'no such event' })
  }
}

async function onNextClick(isCompanyValue: boolean) {
  isCompany.value = isCompanyValue

  sendECommerceBeginCheckoutEvent()

  if (totalValue.value && totalValue.value !== Math.abs(discountValue.value)) {
    isPaymentModalOpen.value = true
  } else if (totalValue.value === Math.abs(discountValue.value)) {
    await submit(false, false, currency.value, false)
  } else {
    await submit(false, false, 'US', true)
  }
}

onServerPrefetch(async () => {
  await load()
})

onMounted(async () => {
  await load()

  removeSessionStorageValue('tickets')
})

onBeforeUnmount(() => {
  order.value = {}
})

function sendECommerceViewCartEvent(): void {
  const googleItems: GoogleECommerceItem[] = Object.keys(order.value).reduce(
    (acc: GoogleECommerceItem[], ticketId: string) => {
      const ticket = ticketsDetailsMap.value[Number(ticketId)]
      const quantity = order.value[ticket.id]

      if (!ticket || !quantity) {
        return acc
      }

      return [
        ...acc,
        getGoogleECommerceItemFromTicket(
          ticket,
          currency.value,
          eventsStore.event,
          quantity
        ),
      ]
    },
    []
  )

  sendViewCartGoogleECommerceEvent(currency.value, googleItems)
}

function sendECommerceQuantityEvent(ticketId: number, quantity: number): void {
  const ticket = ticketsDetailsMap.value[ticketId]

  if (!ticket) {
    return
  }

  const yandexProduct = getYandexECommerceProductFromTicket(
    ticket,
    currency.value,
    eventsStore.event,
    Math.abs(quantity)
  )

  const googleItem = getGoogleECommerceItemFromTicket(
    ticket,
    currency.value,
    eventsStore.event,
    Math.abs(quantity)
  )

  let yandexEvent

  if (quantity > 0) {
    yandexEvent = {
      currencyCode: currency.value,
      [YandexECommerceEventType.Add]: {
        products: [yandexProduct],
      },
    }

    sendAddToCartGoogleECommerceEvent(currency.value, [googleItem])
  } else {
    yandexEvent = {
      currencyCode: currency.value,
      [YandexECommerceEventType.Remove]: {
        products: [yandexProduct],
      },
    }

    sendRemoveFromCartGoogleECommerceEvent(currency.value, [googleItem])
  }

  sendYandexECommerceEvent(yandexEvent)
}

function sendECommerceBeginCheckoutEvent(): void {
  const googleItems: GoogleECommerceItem[] = Object.keys(order.value).reduce(
    (acc: GoogleECommerceItem[], ticketId: string) => {
      const ticket = ticketsDetailsMap.value[Number(ticketId)]
      const quantity = order.value[ticket.id]

      if (!ticket || !quantity) {
        return acc
      }

      return [
        ...acc,
        getGoogleECommerceItemFromTicket(
          ticket,
          currency.value,
          eventsStore.event,
          quantity
        ),
      ]
    },
    []
  )

  sendBeginCheckoutGoogleECommerceEvent(
    {
      currency: currency.value,
      coupon: isPromoCodeApplied.value ? promoCode.value : undefined,
    },
    googleItems
  )
}

function sendECommerceAddPaymentInfoEvent(paymentType: string): void {
  const googleItems: GoogleECommerceItem[] = Object.keys(order.value).reduce(
    (acc: GoogleECommerceItem[], ticketId: string) => {
      const ticket = ticketsDetailsMap.value[Number(ticketId)]
      const quantity = order.value[ticket.id]

      if (!ticket || !quantity) {
        return acc
      }

      return [
        ...acc,
        getGoogleECommerceItemFromTicket(
          ticket,
          currency.value,
          eventsStore.event,
          quantity
        ),
      ]
    },
    []
  )

  sendAddPaymentInfoGoogleECommerceEvent(
    {
      currency: currency.value,
      coupon: isPromoCodeApplied.value ? promoCode.value : undefined,
      payment_type: paymentType,
      value: totalValue.value + discountValue.value / 100,
    },
    googleItems
  )

  reachYandexPaymentGoal(
    paymentType,
    currency.value,
    isPromoCodeApplied.value ? promoCode.value : undefined,
    eventsStore.event?.name,
    (eventsStore.event?.type !== undefined &&
      EventTypeNamesMap[eventsStore.event.type]) ||
      undefined
  )
}

async function reachYandexPaymentGoal(
  type: string,
  currency: string,
  promoCode?: string,
  eventName?: string,
  eventType?: string
) {
  await reachGoal(YandexMetrikaGoals.PaymentStart, {
    payment_start: {
      payment_start_type: type,
      payment_start_currency: currency,
      payment_start_promo_code: promoCode || 'no promo code',
      payment_start_event_name: eventName || 'unknown event',
      payment_start_event_type: eventType || 'unknown event type',
    },
  })
}
</script>
